/**
 * The desktop header's avatar menu: which of the two profiles you are using, and the few
 * places an account goes from here — Профайл, CV, Гарах.
 *
 * It used to be a plain link to /user/profile, where switching between Ажил горилогч and
 * Ажил олгогч sat behind a second menu button. Both now open from the avatar, on every page.
 *
 * "Ажил олгогч" is only a switch for someone who has a company. For anyone else the row
 * says "Компани бүртгүүлэх" and opens the registration dialog instead — so the question is
 * answered before the role changes, not after (Profile.jsx's flow switches first and then
 * asks, which undoes the switch on cancel). Knowing that up front costs one request to
 * /diil/employer/me, made only while this menu is open (useEmployerCompany's `probe`).
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import {
  FiBriefcase, FiChevronRight, FiFileText, FiLoader, FiLogOut, FiPlus, FiShield, FiUser,
} from "react-icons/fi";

import CompanyRegModal from "./CompanyRegModal";
import { useAccountRole } from "../context/RoleContext";
import { useToki } from "../context/TokiContext";
import { useSuperApp } from "../hooks/useSuperApp";
import { useEmployerCompany } from "../hooks/useEmployerCompany";
import { logout as authLogout } from "../api/services/auth";

const rowClass = "w-full flex items-center gap-3 rounded-[14px] px-3 py-2.5 text-left transition-colors hover:bg-[#F8FAFC]";
const iconBox = "keep-light flex-none w-9 h-9 rounded-full flex items-center justify-center overflow-hidden";

/**
 * @param {string|null} avatar the header's own avatar URL (already resolved there)
 * @param {boolean} showAvatar false when that URL failed to load
 * @param {() => void} onAvatarError
 * @param {boolean} [mobile] the phone header's copy: a 40px avatar shown below lg, where the
 *   desktop one is hidden. Same menu either way.
 */
export default function ProfileMenu({ avatar, showAvatar, onAvatarError, mobile = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accountRole, setAccountRole } = useAccountRole();
  const { currentUser } = useSuperApp();
  const { profileData } = useToki();

  const [open, setOpen] = useState(false);
  const [showCompanyReg, setShowCompanyReg] = useState(false);
  const rootRef = useRef(null);

  const {
    companyId, companyName, companyPhoto, admin, platformStaff, isLoading: companyFullyLoading,
    identityLoading, isUnknown, applyResolved,
  } = useEmployerCompany({ probe: open || showCompanyReg });
  // Ready as soon as /diil/employer/me has answered with an admin or a company — the ad list
  // behind it is only needed for someone it left company-less, and waiting for it kept the
  // "Админ руу шилжих" row spinning for as long as every admin ad took to load.
  const companyLoading = identityLoading
    || (companyFullyLoading && !companyId && !admin && !platformStaff);

  // Close on a press outside, or Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const isEmployer = accountRole === "employer";
  // Someone with an employer profile: a company of their own, or a Diil admin / platform
  // staff, who belong to none by design and are employers all the same.
  const hasEmployerProfile = !!companyId || admin || platformStaff;
  // Only "no company" once the server has said so — a request that failed or is still in
  // flight must not offer to register a company the person may already have.
  const needsCompany = !companyLoading && !isUnknown && !hasEmployerProfile;

  const name = currentUser?.custom_name || currentUser?.fullName || currentUser?.name
    || profileData?.name || currentUser?.firstName || profileData?.firstName
    || t("profile.unnamed", "Хэрэглэгч");

  const go = (path) => { setOpen(false); navigate(path); };

  const pickJobseeker = () => {
    setOpen(false);
    setAccountRole("jobseeker");
  };

  const pickEmployer = () => {
    if (companyLoading) return;
    if (needsCompany) {
      setOpen(false);
      setShowCompanyReg(true);
      return;
    }
    setOpen(false);
    if (!isEmployer) setAccountRole("employer");
  };

  const logout = async () => {
    setOpen(false);
    await authLogout();
    navigate("/");
  };

  const employerTitle = admin ? t("roleSelect.adminTitle", "Админ") : t("roleSelect.employerTitle", "Ажил олгогч");
  const employerSub = companyLoading
    ? "…"
    : admin
      ? t("roleSelect.adminDesc", "Платформ удирдах эрхтэй")
      : companyName || t("roleSelect.employerDesc", "Ажлын байрны зар нийтлэнэ, удирдана");

  return (
    <div ref={rootRef} className={`relative ${mobile ? "lg:hidden block" : "lg:block hidden"}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`flex items-center justify-center ${mobile ? "w-10 h-10" : "w-11 h-11"} rounded-full overflow-hidden border-2 transition-colors
          ${open ? "border-[#FFE83E]" : "border-transparent hover:border-[#FFE83E]"}`}
      >
        {showAvatar ? (
          <img src={avatar} alt="profile" className="w-full h-full object-cover" onError={onAvatarError} />
        ) : (
          <div className="w-full h-full bg-[#FFE83E] flex items-center justify-center">
            <FiUser className="text-xl text-[#020618]" />
          </div>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.14 }}
            // Capped to the screen on a phone, where 300px plus the header's edge padding can
            // be wider than the space to the avatar's left.
            className="absolute right-0 top-[calc(100%+10px)] z-50 w-[300px] max-w-[calc(100vw-16px)] origin-top-right rounded-[20px] bg-white
              border border-[#E2E8F0] shadow-[0_12px_40px_rgba(2,6,24,0.16)] p-2"
          >
            {/* The profile in use, on top — pressing it opens it. As an employer that is
                the company: its logo and name, the way Profile.jsx shows it. */}
            <button type="button" role="menuitem" onClick={() => go("/user/profile")}
              className="w-full flex items-center gap-3 rounded-[14px] px-3 py-3 text-left transition-colors hover:bg-[#F8FAFC]">
              <span className={`${iconBox} w-11 h-11 ${isEmployer ? "bg-[#F1F5F9]" : "bg-[#FFE83E]"}`}>
                {isEmployer
                  ? (companyPhoto && !admin
                    ? <img src={companyPhoto} alt="" className="w-full h-full object-cover" />
                    : admin
                      ? <FiShield className="text-[18px] text-[#64748B]" />
                      : <FiBriefcase className="text-[18px] text-[#64748B]" />)
                  : (showAvatar
                    ? <img src={avatar} alt="" className="w-full h-full object-cover" onError={onAvatarError} />
                    : <FiUser className="text-lg text-[#020618]" />)}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[15px] font-extrabold text-[#020618] truncate">
                  {isEmployer && !admin && companyName ? companyName : name}
                </span>
                <span className="block text-[12px] font-semibold text-[#64748B]">
                  {isEmployer ? employerTitle : t("roleSelect.jobseekerTitle", "Ажил горилогч")}
                </span>
              </span>
              <FiChevronRight className="flex-none text-[#94A3B8]" />
            </button>

            {/* The other profile, one row: switch to it — or, for a jobseeker with no
                company, register one. */}
            {isEmployer ? (
              <button type="button" role="menuitem" onClick={pickJobseeker} className={rowClass}>
                <span className={`${iconBox} bg-[#F1F5F9]`}>
                  {showAvatar
                    ? <img src={avatar} alt="" className="w-full h-full object-cover" onError={onAvatarError} />
                    : <FiUser className="text-[17px] text-[#64748B]" />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[14px] font-bold text-[#020618]">{t("profileMenu.toJobseeker", "Ажил горилогч руу шилжих")}</span>
                  <span className="block text-[12px] text-[#94A3B8] truncate">{name}</span>
                </span>
              </button>
            ) : needsCompany ? (
              <button type="button" role="menuitem" onClick={pickEmployer} className={rowClass}>
                <span className={`${iconBox} bg-[#FFE83E]`}>
                  <FiPlus className="text-[18px] text-[#020618]" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[14px] font-bold text-[#020618]">{t("profile.registerCompany", "Компани бүртгүүлэх")}</span>
                  <span className="block text-[12px] text-[#94A3B8] truncate">{t("profile.registerCompanyHint", "Ажил олгогч болж зар нийтлэх")}</span>
                </span>
              </button>
            ) : (
              <button type="button" role="menuitem" onClick={pickEmployer} disabled={companyLoading}
                className={`${rowClass} disabled:cursor-wait`}>
                <span className={`${iconBox} bg-[#F1F5F9]`}>
                  {companyLoading
                    ? <FiLoader className="text-[16px] text-[#94A3B8] animate-spin" />
                    : companyPhoto && !admin
                      ? <img src={companyPhoto} alt="" className="w-full h-full object-cover" />
                      : admin
                        ? <FiShield className="text-[17px] text-[#64748B]" />
                        : <FiBriefcase className="text-[17px] text-[#64748B]" />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[14px] font-bold text-[#020618]">
                    {admin ? t("profileMenu.toAdmin", "Админ руу шилжих") : t("profileMenu.toEmployer", "Ажил олгогч руу шилжих")}
                  </span>
                  <span className="block text-[12px] text-[#94A3B8] truncate">{employerSub}</span>
                </span>
              </button>
            )}

            <div className="my-2 h-px bg-[#F1F5F9]" />

            <button type="button" role="menuitem" onClick={() => go("/user/resume")} className={rowClass}>
              <span className={`${iconBox} bg-[#F8FAFC]`}><FiFileText className="text-[17px] text-[#020618]" /></span>
              <span className="flex-1 text-[14px] font-bold text-[#020618]">{t("profile.viewCv", "CV харах")}</span>
            </button>

            <div className="my-2 h-px bg-[#F1F5F9]" />

            <button type="button" role="menuitem" onClick={logout} className={`${rowClass} hover:bg-[#FEF2F2]`}>
              <span className={`${iconBox} bg-[#FEF2F2]`}><FiLogOut className="text-[17px] text-[#EF4444]" /></span>
              <span className="flex-1 text-[14px] font-bold text-[#EF4444]">{t("auth.logout", "Гарах")}</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <CompanyRegModal
        isOpen={showCompanyReg}
        // Dismissed: nothing to undo — the role was never switched.
        onClose={() => setShowCompanyReg(false)}
        // The modal says what happened itself (registered / joined / already a member).
        onResolved={(company) => {
          setShowCompanyReg(false);
          applyResolved(company);
          setAccountRole("employer");
        }}
        phone={(currentUser?.phone || currentUser?.phone_number || "").replace(/\D/g, "")}
      />
    </div>
  );
}
